import {
	useScrolledFromTopGestures,
	useScrolledFromTopReporter,
} from "@portal/providers/ScrolledFromTopReporterProvider";
import { useCallback, useRef } from "react";
import { cn } from "../../lib/utils";
import { CenteredSpinner } from "../CenteredSpinner";
import { CSVDownload } from "./CSVDownload";
import { DynamicDataTableBody } from "./DynamicDataTableBody";
import { DynamicDataTableHeader } from "./DynamicDataTableHeader";
import { type RowInteraction } from "./types";
import {
	type UseDynamicDataTableStateParams,
	useDynamicDataTableState,
} from "./useDynamicDataTableState";

export type { ServerDynamicDataTableProps } from "./ServerDynamicDataTable";
export { ServerDynamicDataTable } from "./ServerDynamicDataTable";
export type {
	DynamicDataTableBodyCellConfig,
	DynamicDataTableColumn,
	DynamicDataTableColumnFilterKind,
	DynamicDataTableCsvConfig,
	DynamicDataTableHeaderFilterConfig,
} from "./types";

export type DynamicDataTableProps<TData extends object> = UseDynamicDataTableStateParams<TData> &
	RowInteraction<TData> & {
		dataLoading?: boolean;
		enableExportToCSV?: boolean;
		loadingMessage?: string;
		searchString?: string;
		tableClassName?: string;
		minHeight?: number;
		maxHeight?: number;
		/** Size the table to its content instead of filling the parent container. */
		expandToContent?: boolean;
	};

export const DynamicDataTable = <TData extends object>({
	name,
	columns,
	data,
	dataLoading,
	loadingMessage,
	columnVisibility,
	defaultColumnFilters,
	enforcedColumnFilters,
	initialSorting,
	searchString,
	tableClassName,
	enableExportToCSV,
	minHeight,
	maxHeight,
	expandToContent = false,
	...rowInteraction
}: DynamicDataTableProps<TData>) => {
	const { table, rows, columnDefs, rowVirtualizer, parentRef, fitColumnWidthsToContainer } =
		useDynamicDataTableState({
			name,
			columns,
			data,
			columnVisibility,
			defaultColumnFilters,
			enforcedColumnFilters,
			initialSorting,
			searchString,
		});

	const isLoading = data === undefined || (dataLoading ?? false);

	const scrolledFromTopReporting = useScrolledFromTopReporter();
	const { onScroll: handleScroll, attachNativeWheel } = useScrolledFromTopGestures(
		scrolledFromTopReporting?.reportScrolledFromTop ?? null,
		undefined,
		scrolledFromTopReporting?.getHeaderCollapsed,
	);

	const nativeWheelDisposerRef = useRef<(() => void) | undefined>(undefined);
	const setScrollRef = useCallback(
		(node: HTMLDivElement | null) => {
			parentRef.current = node;
			scrolledFromTopReporting?.registerScrollElement(node);
			nativeWheelDisposerRef.current?.();
			nativeWheelDisposerRef.current =
				node && scrolledFromTopReporting ? attachNativeWheel(node) : undefined;
		},
		[parentRef, scrolledFromTopReporting, attachNativeWheel],
	);

	const headerHeight = 33;
	const contentHeight = rowVirtualizer.getTotalSize() + headerHeight + (rows.length > 0 ? 12 : 90);
	const resolvedHeight = expandToContent
		? contentHeight
		: maxHeight
			? Math.min(contentHeight, maxHeight)
			: undefined;
	const containerStyle =
		minHeight && resolvedHeight
			? { height: Math.max(minHeight, resolvedHeight) }
			: { height: resolvedHeight };

	return (
		<div
			className={cn("relative w-full", !expandToContent && !maxHeight && "h-full")}
			style={containerStyle}
		>
			{enableExportToCSV && <CSVDownload name={name} columns={columnDefs} rows={rows} />}
			<div
				className={cn(
					"overflow-hidden h-full w-full relative border border-bc-default rounded-xl",
					tableClassName,
				)}
			>
				<div
					ref={setScrollRef}
					data-testid="dynamic-data-table-scroll-region"
					className="relative h-full w-full overflow-auto bg-surface-light scrollbar-med"
					onScroll={scrolledFromTopReporting ? handleScroll : undefined}
				>
					<div className="w-full" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
						<DynamicDataTableHeader
							headerGroups={table.getHeaderGroups()}
							onDoubleClick={fitColumnWidthsToContainer}
						/>
						{isLoading ? (
							<LoadingMessage loadingMessage={loadingMessage} />
						) : data && rows.length === 0 ? (
							<NoResultsMessage />
						) : (
							<DynamicDataTableBody
								virtualRows={rowVirtualizer.getVirtualItems()}
								rows={rows}
								width={table.getTotalSize()}
								{...rowInteraction}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

const LoadingMessage = ({ loadingMessage }: { loadingMessage?: string }) => {
	return (
		<div
			className={cn(
				loadingMessage
					? "mt-[32px] w-full text-center text-tertiary italic text-[13px] animate-pulse"
					: "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full",
			)}
		>
			{loadingMessage ?? <CenteredSpinner />}
		</div>
	);
};

const NoResultsMessage = () => {
	return (
		<div className="mt-[32px] w-full text-center text-tertiary italic text-[13px]">
			No results found
		</div>
	);
};
